import { app, authentication, meeting } from '@microsoft/teams-js';
import { mapClientType } from './client-type.js';
/**
 * Teams-specific implementation of PlatformAdapter.
 * Uses @microsoft/teams-js v2.x API.
 */
export class TeamsPlatformAdapter {
    _initialized = false;
    _themeCallbacks = [];
    async initialize() {
        if (this._initialized)
            return;
        await app.initialize();
        this._initialized = true;
        // Tell the host we are up. Neither call had ever been made, so nothing
        // ever told Teams that Annoteer had finished loading: the host kept its
        // own loading indicator over our iframe, and when it timed out waiting it
        // replaced the app with "we can't open the app right now". Both were
        // observed on Teams for Android on 2026-08-31, on a cold load after the
        // WebView cache was cleared.
        //
        // notifyAppLoaded hides the spinner; notifySuccess marks the app ready for
        // interaction. Desktop Teams is lenient about this contract and mobile
        // enforces it, which is why a gap this basic survived: every desktop load
        // looked fine, and a warm mobile cache painted fast enough to stay inside
        // the host's timeout. Clearing that cache removed the margin.
        //
        // Fired here rather than after authentication, deliberately. The point is
        // to hand the screen to our own UI as early as possible, so that a stalled
        // sign-in shows the auth banner and its Retry button instead of the host's
        // blank spinner -- which is unreadable, unactionable, and indistinguishable
        // from the app being broken.
        //
        // notifySuccess is NOT awaited. The entire bug being fixed here is a hang,
        // and awaiting a host round-trip on the initialize path would let a slow or
        // unresponsive host stall platformReady, and with it authentication -- the
        // same failure in a new place. Errors are swallowed for the same reason: a
        // host that does not implement these must not take initialization down.
        try {
            app.notifyAppLoaded();
            void app.notifySuccess().catch((err) => {
                console.warn('Teams notifySuccess failed:', err);
            });
        }
        catch (err) {
            console.warn('Teams notifyAppLoaded failed:', err);
        }
        // Register theme change handler
        app.registerOnThemeChangeHandler((theme) => {
            const mapped = this.mapTheme(theme);
            for (const cb of this._themeCallbacks) {
                cb(mapped);
            }
        });
    }
    async getContext() {
        this.ensureInitialized();
        const ctx = await app.getContext();
        return {
            frameContext: this.mapFrameContext(ctx.page?.frameContext),
            clientType: mapClientType(ctx.app?.host?.clientType),
            meetingId: ctx.meeting?.id,
            chatId: ctx.chat?.id,
            theme: this.mapTheme(ctx.app?.theme),
            locale: ctx.app?.locale,
        };
    }
    async getCurrentUser() {
        this.ensureInitialized();
        const ctx = await app.getContext();
        // `??` is wrong here: Teams supplies an EMPTY STRING for user fields it
        // does not populate (several were restricted for privacy), and an empty
        // string is not nullish, so it slips past a `??` fallback and becomes a
        // blank name downstream — a cursor label rendering as an empty bar.
        // Fall back through login hint before giving up, since the local part of
        // an address identifies a person far better than "Unknown User".
        const loginHint = ctx.user?.loginHint?.trim();
        const contextName = ctx.user?.displayName?.trim();
        return {
            id: ctx.user?.id?.trim() || 'unknown',
            displayName: contextName || loginHint?.split('@')[0] || 'Unknown User',
            email: loginHint,
        };
    }
    async getAuthToken() {
        this.ensureInitialized();
        return authentication.getAuthToken();
    }
    async shareToStage(contentUrl) {
        this.ensureInitialized();
        return new Promise((resolve, reject) => {
            meeting.shareAppContentToStage((error) => {
                if (error) {
                    reject(new Error(`Failed to share to stage: ${error.message}`));
                }
                else {
                    resolve();
                }
            }, contentUrl);
        });
    }
    async isAppSharingToStage() {
        try {
            this.ensureInitialized();
        }
        catch {
            return false;
        }
        return new Promise((resolve) => {
            try {
                meeting.getAppContentStageSharingState((error, state) => {
                    resolve(!error && state?.isAppSharing === true);
                });
            }
            catch {
                resolve(false);
            }
        });
    }
    /**
     * Open a URL outside the Teams app frame.
     *
     * Uses `app.openLink`, which hands the URL to the host so it lands in the
     * user's real browser. This is required for Stripe Checkout: it sets
     * frame-ancestors and simply will not render inside the Teams iframe, and
     * navigating the frame itself would tear down the meeting app mid-meeting.
     *
     * Falls back to `window.open` when the Teams SDK is unavailable (browser
     * dev). `noopener` is set because the payment page must not retain a handle
     * on our window.
     */
    async openExternalLink(url) {
        try {
            this.ensureInitialized();
            await app.openLink(url);
        }
        catch (err) {
            if (typeof window === 'undefined')
                throw err;
            const opened = window.open(url, '_blank', 'noopener,noreferrer');
            if (!opened) {
                // Popup blocked — the caller should surface the URL so the user can
                // open it manually rather than silently doing nothing.
                throw new Error('Could not open the link — your browser may be blocking pop-ups.');
            }
        }
    }
    onThemeChange(callback) {
        this._themeCallbacks.push(callback);
    }
    async getMeetingRole() {
        this.ensureInitialized();
        const ctx = await app.getContext();
        // Meeting role detection via context
        // In a real implementation, you'd use the Live Share SDK for role checking
        if (ctx.meeting?.id) {
            return 'presenter'; // Default to presenter for now
        }
        return 'unknown';
    }
    isSupported() {
        try {
            return app.isInitialized();
        }
        catch {
            return false;
        }
    }
    ensureInitialized() {
        if (!this._initialized) {
            throw new Error('TeamsPlatformAdapter not initialized. Call initialize() first.');
        }
    }
    mapFrameContext(frameContext) {
        switch (frameContext) {
            case 'sidePanel':
                return 'sidePanel';
            case 'meetingStage':
                return 'meetingStage';
            case 'content':
                return 'content';
            case 'settings':
                return 'settings';
            default:
                return 'unknown';
        }
    }
    mapTheme(theme) {
        switch (theme) {
            case 'dark':
                return 'dark';
            case 'contrast':
                return 'contrast';
            default:
                return 'default';
        }
    }
}
//# sourceMappingURL=TeamsPlatformAdapter.js.map